import { Box, Button, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Department, UserResponse } from '@/api/generated/models';
import {
  getListTeamsQueryKey,
  listTeams,
  useSearchDepartments
} from '@/api/generated/departments-teams/departments-teams';
import { useListUsers } from '@/api/generated/users/users';
import {
  CardSection,
  ListPagination,
  MobileFab,
  PageHeading,
  SearchField,
  tableRowStyle
} from '@/components/common';
import { DepartmentFormModal } from '@/features/departments/components/DepartmentFormModal';
import { usePageSize } from '@/utils/usePageSize';

const detailsLinkStyle = {
  color: 'var(--color-accent-primary-active)',
  textDecoration: 'underline'
} as const;

export const DepartmentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [createModalOpened, setCreateModalOpened] = useState(false);
  // Ref, ne state — onSaved i onCreated se pozivaju sinkrono unutar istog
  // onSuccess handlera u modalu, prije nego bi state ažuriranje uopće stiglo
  // na sljedeći render; navigate() odmah zatim treba tu poruku odmah dostupnu.
  const createdMessageRef = useRef('');

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  const { data, isLoading } = useSearchDepartments({
    page,
    pageSize,
    search: debouncedSearch.trim() || undefined
  });
  const pageItems = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Department/Team nemaju agregatna polja (broj timova/članova) — backend
  // to ne izlaže kao dio Department zapisa, pa se broji na frontendu, samo za
  // trenutno prikazanu stranicu: jedan batch poziva za timove po odjelu
  // (useQueries, isti obrazac kao InvoiceDetailPage) + jedan poziv za sve
  // korisnike organizacije, pa se članovi grupiraju po team.id.
  const teamQueries = useQueries({
    queries: pageItems.map((department) => ({
      queryKey: getListTeamsQueryKey(department.id),
      queryFn: () => listTeams(department.id ?? ''),
      enabled: !!department.id
    }))
  });

  const { data: usersData } = useListUsers(undefined, { query: { refetchOnMount: 'always' } });
  const users: UserResponse[] = usersData ?? [];
  const memberCountByTeam = useMemo(() => {
    const map = new Map<string, number>();
    users.forEach((user) => {
      if (!user.teamId) return;
      map.set(user.teamId, (map.get(user.teamId) ?? 0) + 1);
    });
    return map;
  }, [users]);

  const departmentStats = useMemo(() => {
    const map = new Map<string, { teamCount: number; memberCount: number }>();
    pageItems.forEach((department, index) => {
      if (!department.id) return;
      const teams = teamQueries[index]?.data ?? [];
      const memberCount = teams.reduce(
        (sum, team) => sum + (team.id ? (memberCountByTeam.get(team.id) ?? 0) : 0),
        0
      );
      map.set(department.id, { teamCount: teams.length, memberCount });
    });
    return map;
  }, [pageItems, teamQueries, memberCountByTeam]);

  const paginationLabel = t('departments.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const goToDetail = (id?: string) => {
    if (id) navigate(`/users/departments/${id}`);
  };

  const columns: [string, number][] = [
    ['departments.colName', 220],
    ['departments.colTeams', 220],
    ['departments.colMembers', 140]
  ];

  const renderRow = (department: Department) => {
    const stats = department.id ? departmentStats.get(department.id) : undefined;
    return { stats };
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <Group justify="space-between" wrap="nowrap" visibleFrom="sm">
          <PageHeading title={t('departments.title')} subtitle={t('departments.subtitle')} />
          <Button onClick={() => setCreateModalOpened(true)}>
            + {t('departments.newDepartment')}
          </Button>
        </Group>

        <Stack gap={12} hiddenFrom="sm">
          <PageHeading title={t('departments.title')} subtitle={t('departments.subtitle')} />
          <MobileFab onClick={() => setCreateModalOpened(true)}>
            + {t('departments.newDepartment')}
          </MobileFab>
        </Stack>

        <Group gap={16} align="flex-end" wrap="wrap">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('departments.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('departments.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <>
                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '12px 20px',
                    backgroundColor: 'var(--color-bg-canvas)',
                    borderBottom: '1px solid var(--color-border-soft)'
                  }}
                >
                  {columns.map(([key, width]) => (
                    <Text
                      key={key}
                      fz={10}
                      fw={600}
                      w={width}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t(key)}
                    </Text>
                  ))}
                  <Text
                    fz={10}
                    fw={600}
                    w={100}
                    ta="right"
                    style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                  >
                    {t('departments.colAction')}
                  </Text>
                </div>

                {pageItems.map((department, index) => {
                  const { stats } = renderRow(department);
                  return (
                    <div
                      key={department.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text
                        fz={13.5}
                        fw={600}
                        w={220}
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {department.name}
                      </Text>
                      <Text
                        fz={13}
                        w={220}
                        style={{
                          color: 'var(--color-text-secondary)',
                          fontFamily: 'var(--mantine-font-family-monospace)'
                        }}
                      >
                        {stats?.teamCount ?? '—'}
                      </Text>
                      <Text
                        fz={13}
                        w={140}
                        style={{
                          color: 'var(--color-text-muted)',
                          fontFamily: 'var(--mantine-font-family-monospace)'
                        }}
                      >
                        {stats?.memberCount ?? '—'}
                      </Text>
                      <Text
                        fz={12}
                        fw={600}
                        w={100}
                        ta="right"
                        role="button"
                        tabIndex={0}
                        onClick={() => goToDetail(department.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') goToDetail(department.id);
                        }}
                        style={{ ...detailsLinkStyle, cursor: 'pointer' }}
                      >
                        {t('departments.detailsLink')}
                      </Text>
                    </div>
                  );
                })}

                <ListPagination
                  label={paginationLabel}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('departments.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((department) => {
                const { stats } = renderRow(department);
                return (
                  <Stack
                    key={department.id}
                    gap={14}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-default)',
                      backgroundColor: 'var(--color-bg-surface)'
                    }}
                  >
                    <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {department.name}
                    </Text>
                    <Stack gap={6}>
                      <Group justify="space-between" wrap="nowrap">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('departments.colTeams')}
                        </Text>
                        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                          {stats?.teamCount ?? '—'}
                        </Text>
                      </Group>
                      <Group justify="space-between" wrap="nowrap">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('departments.colMembers')}
                        </Text>
                        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                          {stats?.memberCount ?? '—'}
                        </Text>
                      </Group>
                    </Stack>
                    <Text
                      fz={12.5}
                      fw={600}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToDetail(department.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') goToDetail(department.id);
                      }}
                      style={{ ...detailsLinkStyle, cursor: 'pointer' }}
                    >
                      {t('departments.detailsLink')}
                    </Text>
                  </Stack>
                );
              })}

              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>

      <DepartmentFormModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        onSaved={(message) => {
          createdMessageRef.current = message;
        }}
        onCreated={(id) =>
          navigate(`/users/departments/${id}`, {
            state: { savedMessage: createdMessageRef.current }
          })
        }
      />
    </Container>
  );
};
